import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { captureException } from '@sentry/react-native';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useEffect, useState } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { Linking, Modal, Platform, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ProgressBar } from 'react-native-paper';

import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import CloseButton from '../buttons/CloseButton';
import Text from '../Text';

export type FileInputFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  showError?: boolean;
  progress?: number;
  indeterminateProgress?: boolean;
  getBlob?: boolean;
  acceptedTypes?: string[];
  maxFileSize?: number; // On MB
  allowCamera?: boolean;
};

const FileInputField = <T extends FieldValues>({
  name,
  control,
  showError = true,
  progress,
  indeterminateProgress = false,
  getBlob = false,
  acceptedTypes = ['application/pdf'],
  maxFileSize,
  allowCamera = false
}: FileInputFieldProps<T>) => {
  const { showError: errorModal } = useEndFlowModal();

  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController<T>({
    name,
    control
  });

  useEffect(() => {
    if (value) setFileName(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fileName, setFileName] = useState<string | null>(null);
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(false);

  useEffect(() => {
    if (!allowCamera || Platform.OS === 'web') return;

    setCameraAvailable(true);
  }, [allowCamera]);

  const pickFromDocuments = async () => {
    const request = await DocumentPicker.getDocumentAsync({
      type: acceptedTypes
    });
    if (!request.canceled && request.assets[0]) {
      if (maxFileSize) {
        try {
          const fileSize =
            Platform.OS === 'web'
              ? (request.output?.item(0)?.size ?? 0) / (1024 * 1024)
              : (request.assets.at(0)?.size ?? 0) / (1024 * 1024);

          if (fileSize > maxFileSize) {
            errorModal({
              title: i18n.t('mix.err_size_file_title', { MAX_FILE_SIZE: maxFileSize }),
              message: i18n.t('mix.err_size_file', {
                MAX_FILE_SIZE: maxFileSize,
                FILE_SIZE: fileSize.toFixed(2)
              })
            });
            return;
          }
        } catch (error) {
          errorModal({
            title: i18n.t('mix.err_getting_file_size')
          });
          console.error('Error getting file size:', error);
          return;
        }
      }
      if (Platform.OS === 'web' && getBlob) {
        onChange(request.output?.item(0));
        setFileName(request.assets[0].name);
      } else {
        onChange(request.assets[0].uri);
        setFileName(request.assets[0].name);
      }
    }
  };

  const pickFromCamera = async () => {
    try {
      const permissions = await ImagePicker.getCameraPermissionsAsync();

      const launchCamera = async () => {
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: 'images',
          allowsEditing: true,
          aspect: [4, 4]
        });

        if (!result.canceled && result.assets[0]) {
          onChange(result.assets[0].uri);
          setFileName(result.assets[0].fileName ?? i18n.t('mix.camera'));
        }
      };

      if (!permissions?.granted && permissions?.canAskAgain) {
        const granted = await ImagePicker.requestCameraPermissionsAsync();
        if (granted.granted) {
          await launchCamera();
        }
      } else if (permissions?.granted) {
        await launchCamera();
      } else {
        Linking.openSettings();
      }
    } catch (e) {
      captureException(e);
      errorModal({ title: i18n.t('mix.err_taking_img') });
    }
  };

  const closeAndPick = (source: 'camera' | 'file') => {
    setShowSourcePicker(false);
    setTimeout(() => {
      if (source === 'camera') {
        pickFromCamera();
      } else {
        pickFromDocuments();
      }
    }, 400);
  };

  const uploadPress = () => {
    if (allowCamera && cameraAvailable) {
      setShowSourcePicker(true);
    } else {
      pickFromDocuments();
    }
  };

  return (
    <View>
      <Modal
        visible={showSourcePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSourcePicker(false)}>
        <View style={styles.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setShowSourcePicker(false)} />
          <View style={styles.modalContent}>
            <CloseButton style={styles.closeButton} onCloseTap={() => setShowSourcePicker(false)} />
            <Text fontWeight="900" style={styles.selectTypeHeader}>
              {i18n.t('mix.choose_from')}
            </Text>
            <View style={styles.buttonsContainer}>
              <BrandedButton style={styles.flexOne} onPress={() => closeAndPick('file')}>
                <MaterialIcons name="insert-drive-file" color="white" />
                {` ${i18n.t('mix.browse_files')}`}
              </BrandedButton>
              {cameraAvailable && (
                <BrandedButton style={styles.flexOne} onPress={() => closeAndPick('camera')}>
                  <MaterialIcons name="add-a-photo" color="white" />
                  {` ${i18n.t('mix.camera')}`}
                </BrandedButton>
              )}
            </View>
          </View>
        </View>
      </Modal>

      <View style={styles.mainContainer}>
        <TouchableOpacity
          disabled={progress !== undefined || indeterminateProgress}
          onPress={uploadPress}
          style={styles.uploadButton}>
          <AntDesign name="upload" size={24} color="white" />
        </TouchableOpacity>
        {value && fileName && (
          <>
            <Text numberOfLines={2} style={styles.statusText}>
              {fileName}
            </Text>
            <TouchableOpacity
              disabled={progress !== undefined || indeterminateProgress}
              onPress={() => {
                setFileName(null);
                onChange(null);
              }}>
              <MaterialIcons name="delete" size={24} color="red" />
            </TouchableOpacity>
          </>
        )}
      </View>

      {progress !== undefined && progress >= 0 && progress <= 100 && (
        <ProgressBar style={styles.progressBar} animatedValue={progress / 100} color="gold" />
      )}
      {indeterminateProgress && <ProgressBar style={styles.progressBar} indeterminate color="gold" />}
      {progress === 100 && <Text style={styles.labelText}>{i18n.t('mix.file_uploaded')}</Text>}
      {!error && (value === undefined || !value) && <Text style={styles.labelText}>{i18n.t('mix.tap_to_upload')}</Text>}
      {showError && !!error?.message && (
        <Text fontWeight="700" style={styles.errorText}>
          {error.message}
        </Text>
      )}
    </View>
  );
};

export default FileInputField;

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  errorText: {
    marginTop: 15,
    fontSize: 14,
    color: 'red'
  },

  labelText: {
    marginTop: 15,
    color: 'black'
  },

  uploadButton: {
    backgroundColor: 'black',
    width: 54,
    height: 54,
    borderRadius: 27,
    justifyContent: 'center',
    alignItems: 'center'
  },

  statusText: {
    flex: 1,
    paddingHorizontal: 10
  },

  progressBar: {
    marginTop: 10,
    backgroundColor: 'gray'
  },

  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center'
  },

  modalContent: {
    backgroundColor: 'white',
    paddingHorizontal: 30,
    paddingVertical: 25,
    width: '85%',
    maxWidth: 420,
    borderRadius: 10
  },

  closeButton: {
    position: 'absolute',
    left: 8,
    top: 8,
    zIndex: 1
  },

  selectTypeHeader: {
    fontSize: 22,
    color: 'black',
    justifyContent: 'center',
    alignSelf: 'center',
    textAlign: 'center',
    marginBottom: 20
  },

  buttonsContainer: {
    flexDirection: 'row',
    gap: 15
  },

  flexOne: {
    flex: 1
  }
});
